// 内容审核 - 原型数据层（内存 mock）
// 说明：后端未开发，先用内存数据模拟接口，函数签名按 api 风格设计（均返回 Promise）。
// 后端就绪后，将各函数内部替换为 props.createApiClient().get<T>() / post<T>() 等调用即可。

import type {
  AddTermInput,
  LexiconCategory,
  MachineTag,
  ReviewAction,
  ReviewLexicon,
  ReviewLog,
  ReviewPolicy,
  ReviewTerm,
  SaveLexiconInput,
  SavePolicyInput
} from './types'

const delay = (ms = 120) => new Promise((resolve) => setTimeout(resolve, ms))

function pad(value: number) {
  return String(value).padStart(2, '0')
}

// 以当前时间倒退 minutes 分钟，生成 YYYY-MM-DD HH:mm:ss（保证日志筛选示例始终有数据）
function stamp(minutesAgo: number) {
  const d = new Date(Date.now() - minutesAgo * 60000)
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

// —— 内存数据 ——

const lexicons: ReviewLexicon[] = [
  { id: 1, name: '补充暴力', result: 'VIOLATION', matchMode: 'EXACT', category: 'CUSTOM', termCount: 2, updatedAt: stamp(1200) },
  { id: 2, name: '赞美祖国放行词库', result: 'ALLOW', matchMode: 'FUZZY', category: 'POLITICS', termCount: 3, updatedAt: stamp(1500) },
  { id: 3, name: '反动暴乱民族歧视敏感内容', result: 'VIOLATION', matchMode: 'FUZZY', category: 'POLITICS', termCount: 6, updatedAt: stamp(2800) },
  { id: 4, name: '涉黄违禁词库', result: 'VIOLATION', matchMode: 'FUZZY', category: 'PORN', termCount: 8, updatedAt: stamp(4200) },
  { id: 5, name: '暴恐安全词库', result: 'SUSPECTED', matchMode: 'EXACT', category: 'TERROR', termCount: 0, updatedAt: stamp(6000) }
]

const terms: ReviewTerm[] = [
  { id: 28744688, lexiconId: 1, term: '台独', machineTags: ['POLITICS'], type: 'NORMAL', remark: '', createdAt: stamp(1200) },
  { id: 28744689, lexiconId: 1, term: '一刀999', machineTags: ['AD', 'SPAM'], type: 'NORMAL', remark: '广告诱导充值', createdAt: stamp(1150) },
  { id: 28744690, lexiconId: 2, term: '伟大的祖国', machineTags: ['VALUES'], type: 'COMPOSITE', remark: '正向宣传词', createdAt: stamp(1500) },
  { id: 28744691, lexiconId: 2, term: '盛世中华', machineTags: ['VALUES'], type: 'NORMAL', remark: '', createdAt: stamp(1490) },
  { id: 28744692, lexiconId: 2, term: 'china', machineTags: ['VALUES'], type: 'ENGLISH', remark: '', createdAt: stamp(1480) },
  { id: 28744693, lexiconId: 3, term: '某分裂组织', machineTags: ['POLITICS', 'TERROR'], type: 'COMPOSITE', remark: '平台基线词', createdAt: stamp(2800) },
  { id: 28744694, lexiconId: 3, term: '颠覆政权', machineTags: ['POLITICS', 'ILLEGAL'], type: 'NORMAL', remark: '平台基线词', createdAt: stamp(2790) },
  { id: 28744695, lexiconId: 4, term: '一夜情', machineTags: ['PORN'], type: 'NORMAL', remark: '', createdAt: stamp(4200) },
  { id: 28744696, lexiconId: 4, term: '成人视频', machineTags: ['PORN', 'AD'], type: 'NORMAL', remark: '', createdAt: stamp(4190) }
]

let nextLexiconId = 100
let nextTermId = 28744700
let nextPolicySeq = 3

const policies: ReviewPolicy[] = [
  {
    id: 'cl-01',
    name: '直播间发言规则',
    scene: '直播弹幕发言',
    service: 'TEXT',
    linkedLexiconIds: [1, 2],
    status: 'ACTIVE',
    updatedAt: stamp(800)
  },
  {
    id: 'cl-02',
    name: '图文评论规则',
    scene: '文章评论',
    service: 'TEXT',
    linkedLexiconIds: [3, 4],
    status: 'ACTIVE',
    updatedAt: stamp(2000)
  },
  {
    id: 'cl-03',
    name: '私信内容规则',
    scene: '用户私信',
    service: 'TEXT',
    linkedLexiconIds: [],
    status: 'DISABLED',
    updatedAt: stamp(3600)
  }
]

const logs: ReviewLog[] = [
  {
    id: 'LOG001', module: '直播间', content: '看到台独分子我就要砍', requestId: 'de6e6708-f978-4c60-9c00-269dcbeb6684',
    hitTerm: '台独', hitPolicy: '直播间发言规则', action: 'BLOCK', machineTag: 'POLITICS', service: 'TEXT',
    reviewedAt: stamp(5), submittedAt: stamp(6), tenant: '国学', submitter: '林可儿', hitPosition: 3
  },
  {
    id: 'LOG002', module: '图文', content: '这是一条测试信息，台湾是中国一个省', requestId: 'a1b2c3d4-e5f6-7890-abcd-ef0123456789',
    hitTerm: '台湾', hitPolicy: '图文评论规则', action: 'HUMAN', machineTag: 'POLITICS', service: 'TEXT',
    reviewedAt: stamp(70), submittedAt: stamp(72), tenant: '国学', submitter: '张明', hitPosition: 10
  },
  {
    id: 'LOG003', module: '直播间', content: '点链接加群领百万福利', requestId: '9f8e7d6c-5b4a-3210-fedc-ba9876543210',
    hitTerm: '福利', hitPolicy: '直播间发言规则', action: 'BLOCK', machineTag: 'AD', service: 'TEXT',
    reviewedAt: stamp(300), submittedAt: stamp(302), tenant: '直播', submitter: '系统', hitPosition: 12
  },
  {
    id: 'LOG004', module: '图文', content: '这个推荐真的不错，值得一看', requestId: '11111111-2222-3333-4444-555555555555',
    hitTerm: '', hitPolicy: '-', action: 'PASS', machineTag: 'SPAM', service: 'TEXT',
    reviewedAt: stamp(1200), submittedAt: stamp(1201), tenant: '国学', submitter: '系统', hitPosition: 0
  },
  {
    id: 'LOG005', module: '直播', content: '快来加群，有惊喜', requestId: 'aaaa-bbbb-cccc-dddd-eeeeeeeeeeee',
    hitTerm: '加群', hitPolicy: '直播间发言规则', action: 'HUMAN', machineTag: 'SPAM', service: 'TEXT',
    reviewedAt: stamp(3000), submittedAt: stamp(3001), tenant: '直播', submitter: '李雷', hitPosition: 5
  }
]

// —— 词库 ——

export async function listLexicons(keyword = ''): Promise<ReviewLexicon[]> {
  await delay()
  const kw = keyword.trim()
  return lexicons
    .filter((item) => !kw || item.name.includes(kw))
    .map((item) => ({ ...item }))
}

export async function createLexicon(input: SaveLexiconInput): Promise<ReviewLexicon> {
  await delay()
  const record: ReviewLexicon = {
    id: nextLexiconId++,
    name: input.name,
    result: input.result,
    matchMode: input.matchMode,
    category: input.category,
    termCount: 0,
    updatedAt: stamp(0)
  }
  lexicons.unshift(record)
  return { ...record }
}

export async function updateLexicon(id: number, input: SaveLexiconInput): Promise<ReviewLexicon> {
  await delay()
  const target = lexicons.find((item) => item.id === id)
  if (!target) throw new Error('词库不存在')
  target.name = input.name
  target.result = input.result
  target.updatedAt = stamp(0)
  return { ...target }
}

export async function deleteLexicon(id: number): Promise<void> {
  await delay()
  const index = lexicons.findIndex((item) => item.id === id)
  if (index >= 0) lexicons.splice(index, 1)
  for (let i = terms.length - 1; i >= 0; i -= 1) {
    if (terms[i].lexiconId === id) terms.splice(i, 1)
  }
}

export async function listTerms(lexiconId: number, keyword = '', tag: MachineTag | '' = ''): Promise<ReviewTerm[]> {
  await delay()
  const kw = keyword.trim()
  return terms
    .filter((item) => item.lexiconId === lexiconId)
    .filter((item) => !kw || item.term.includes(kw))
    .filter((item) => !tag || item.machineTags.includes(tag))
    .map((item) => ({ ...item, machineTags: [...item.machineTags] }))
}

export async function addTerms(input: AddTermInput): Promise<void> {
  await delay()
  const target = lexicons.find((item) => item.id === input.lexiconId)
  if (!target) throw new Error('词库不存在')
  for (const term of input.terms) {
    terms.push({
      id: nextTermId++,
      lexiconId: input.lexiconId,
      term,
      machineTags: [...input.machineTags],
      type: input.type,
      remark: input.remark,
      createdAt: stamp(0)
    })
  }
  target.termCount = terms.filter((item) => item.lexiconId === input.lexiconId).length
  target.updatedAt = stamp(0)
}

export async function deleteTerms(lexiconId: number, termIds: number[]): Promise<void> {
  await delay()
  const idSet = new Set(termIds)
  for (let i = terms.length - 1; i >= 0; i -= 1) {
    if (terms[i].lexiconId === lexiconId && idSet.has(terms[i].id)) terms.splice(i, 1)
  }
  const target = lexicons.find((item) => item.id === lexiconId)
  if (target) {
    target.termCount = terms.filter((item) => item.lexiconId === lexiconId).length
    target.updatedAt = stamp(0)
  }
}

// 策略配置页「关联词库」：按分类分组
export async function listLexiconsByCategory(): Promise<Array<{ category: LexiconCategory; items: ReviewLexicon[] }>> {
  await delay()
  return ['POLITICS', 'PORN', 'TERROR', 'CUSTOM'].map((category) => ({
    category: category as LexiconCategory,
    items: lexicons.filter((item) => item.category === category).map((item) => ({ ...item }))
  }))
}

// —— 策略 ——

export async function listPolicies(keyword = ''): Promise<ReviewPolicy[]> {
  await delay()
  const kw = keyword.trim()
  return policies
    .filter((item) => !kw || item.id.includes(kw) || item.name.includes(kw))
    .map((item) => ({ ...item, linkedLexiconIds: [...item.linkedLexiconIds] }))
}

export async function createPolicy(input: SavePolicyInput): Promise<ReviewPolicy> {
  await delay()
  const record: ReviewPolicy = {
    id: `cl-${String(nextPolicySeq).padStart(2, '0')}`,
    ...input,
    linkedLexiconIds: [...input.linkedLexiconIds],
    updatedAt: stamp(0)
  }
  nextPolicySeq += 1
  policies.unshift(record)
  return { ...record, linkedLexiconIds: [...record.linkedLexiconIds] }
}

export async function duplicatePolicy(id: string): Promise<ReviewPolicy> {
  await delay()
  const source = policies.find((item) => item.id === id)
  if (!source) throw new Error('策略不存在')
  const record: ReviewPolicy = {
    ...source,
    id: `cl-${String(nextPolicySeq).padStart(2, '0')}`,
    name: `${source.name}（副本）`,
    status: 'DISABLED',
    linkedLexiconIds: [...source.linkedLexiconIds],
    updatedAt: stamp(0)
  }
  nextPolicySeq += 1
  policies.unshift(record)
  return { ...record, linkedLexiconIds: [...record.linkedLexiconIds] }
}

export async function updatePolicy(id: string, input: SavePolicyInput): Promise<ReviewPolicy> {
  await delay()
  const target = policies.find((item) => item.id === id)
  if (!target) throw new Error('策略不存在')
  target.name = input.name
  target.scene = input.scene
  target.service = input.service
  target.linkedLexiconIds = [...input.linkedLexiconIds]
  target.status = input.status
  target.updatedAt = stamp(0)
  return { ...target, linkedLexiconIds: [...target.linkedLexiconIds] }
}

export async function deletePolicy(id: string): Promise<void> {
  await delay()
  const index = policies.findIndex((item) => item.id === id)
  if (index >= 0) policies.splice(index, 1)
}

// —— 日志 ——

export interface LogFilter {
  action: ReviewAction | ''
  tag: MachineTag | ''
  keyword: string
  from: string
  to: string
}

export async function listLogs(filter: LogFilter): Promise<ReviewLog[]> {
  await delay()
  const kw = filter.keyword.trim()
  return logs
    .filter((item) => !filter.action || item.action === filter.action)
    .filter((item) => !filter.tag || item.machineTag === filter.tag)
    .filter((item) => !kw || item.module.includes(kw) || item.content.includes(kw) || item.requestId.includes(kw))
    // 提交时间按日比较（from/to 为 YYYY-MM-DD），保证当天边界不丢数据
    .filter((item) => !filter.from || item.reviewedAt.slice(0, 10) >= filter.from)
    .filter((item) => !filter.to || item.reviewedAt.slice(0, 10) <= filter.to)
    .map((item) => ({ ...item }))
}

export async function getLog(id: string): Promise<ReviewLog | null> {
  await delay()
  const target = logs.find((item) => item.id === id)
  return target ? { ...target } : null
}
